/**
 * Client Engagement Lead (CEL) Onboarding Wizard
 * Week 1 Day 1: Client onboarding flow
 * 
 * This wizard guides the CEL through:
 * 1. Welcome & engagement overview
 * 2. Review & refine High-Level Objectives (HLOs)
 * 3. Invite team members for discovery
 * 4. Completion & next steps
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { CheckCircle, ArrowRight, Users, Target, Rocket, X, Plus, Trash2, Edit2, Mail } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';

interface HLO {
  id: string;
  text: string;
  category: 'efficiency' | 'automation' | 'user_experience' | 'reporting' | 'compliance' | 'integration' | 'other';
  source: 'ai_extracted' | 'client_added' | 'consultant_added';
  confidence?: number;
  status: 'draft' | 'approved' | 'removed';
}

interface TeamMember {
  name: string;
  email: string;
  role?: string;
}

interface CELOnboardingWizardProps {
  engagementId: string;
  engagementName: string;
  platform: string;
  consultantName: string;
  consultantEmail: string;
  initialHLOs?: HLO[];
  onComplete: (data: {
    approvedHLOs: HLO[];
    teamMembers: TeamMember[];
  }) => void;
  onSkip?: () => void;
}

export function CELOnboardingWizard({
  engagementId,
  engagementName,
  platform,
  consultantName,
  consultantEmail,
  initialHLOs = [],
  onComplete,
  onSkip
}: CELOnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [hlos, setHLOs] = useState<HLO[]>(initialHLOs);
  const [editingHLO, setEditingHLO] = useState<string | null>(null);
  const [newHLOText, setNewHLOText] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('');

  const categoryColors: Record<HLO['category'], string> = {
    efficiency: 'bg-blue-100 text-blue-800',
    automation: 'bg-green-100 text-green-800',
    user_experience: 'bg-purple-100 text-purple-800',
    reporting: 'bg-orange-100 text-orange-800',
    compliance: 'bg-red-100 text-red-800',
    integration: 'bg-yellow-100 text-yellow-800',
    other: 'bg-gray-100 text-gray-800'
  };

  const addHLO = () => {
    if (!newHLOText.trim()) return;

    const newHLO: HLO = {
      id: `hlo_${Date.now()}`,
      text: newHLOText,
      category: 'other',
      source: 'client_added',
      status: 'approved'
    };

    setHLOs([...hlos, newHLO]);
    setNewHLOText('');
  };

  const removeHLO = (id: string) => {
    setHLOs(hlos.map(hlo => 
      hlo.id === id ? { ...hlo, status: 'removed' as const } : hlo
    ));
  };

  const approveHLO = (id: string) => {
    setHLOs(hlos.map(hlo => 
      hlo.id === id ? { ...hlo, status: 'approved' as const } : hlo
    ));
  };

  const updateHLO = (id: string, text: string) => {
    setHLOs(hlos.map(hlo => 
      hlo.id === id ? { ...hlo, text } : hlo
    ));
    setEditingHLO(null);
  };

  const addTeamMember = () => {
    if (!newMemberName.trim() || !newMemberEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    setTeamMembers([
      ...teamMembers,
      {
        name: newMemberName,
        email: newMemberEmail,
        role: newMemberRole || undefined
      }
    ]);

    setNewMemberName('');
    setNewMemberEmail('');
    setNewMemberRole('');
  };

  const removeTeamMember = (index: number) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  };

  const handleComplete = () => {
    const approvedHLOs = hlos.filter(hlo => hlo.status === 'approved');
    onComplete({
      approvedHLOs,
      teamMembers
    });
  };

  const activeHLOs = hlos.filter(hlo => hlo.status !== 'removed');
  const approvedHLOs = hlos.filter(hlo => hlo.status === 'approved');

  const steps = [
    {
      title: 'Welcome to Your Project',
      icon: Rocket,
      content: (
        <div className="space-y-4">
          <p>
            Welcome to <strong>{engagementName}</strong>!
          </p>
          <p>
            You've been selected as the <strong>Client Engagement Lead</strong> for this {platform} implementation project. 
            Your consultant, {consultantName}, has set up this engagement and is ready to work with you.
          </p>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold mb-2">Your Role as Client Engagement Lead</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Review and refine the project objectives</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Invite your team members to participate in discovery</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Guide your team through providing discovery information</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Review implementation progress with your consultant</span>
              </li>
            </ul>
          </div>

          <Alert>
            <AlertDescription>
              This onboarding wizard will take about 15-20 minutes. You can pause and come back anytime.
            </AlertDescription>
          </Alert>

          <div className="bg-gray-50 p-3 rounded-lg text-sm">
            <p className="text-gray-600">
              <strong>Your Consultant:</strong> {consultantName}<br />
              <strong>Contact:</strong> <a href={`mailto:${consultantEmail}`} className="text-blue-600 hover:underline">{consultantEmail}</a>
            </p>
          </div>
        </div>
      )
    },
    {
      title: 'Review Project Objectives',
      icon: Target,
      content: (
        <div className="space-y-4">
          <p>
            We've analyzed your Statement of Work and identified the following <strong>high-level objectives</strong>. 
            Please review, edit, or add to these objectives to ensure they accurately reflect your goals.
          </p>

          {activeHLOs.length === 0 ? (
            <Alert>
              <AlertDescription>
                No objectives have been extracted yet. You can add your own objectives below.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {activeHLOs.map((hlo) => (
                <div
                  key={hlo.id}
                  className={`p-4 rounded-lg border ${
                    hlo.status === 'approved' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      {editingHLO === hlo.id ? (
                        <Textarea
                          defaultValue={hlo.text}
                          className="mb-2"
                          onBlur={(e) => updateHLO(hlo.id, e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <p className="text-sm">{hlo.text}</p>
                      )}
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className={categoryColors[hlo.category]}>
                          {hlo.category.replace('_', ' ')}
                        </Badge>
                        {hlo.source === 'ai_extracted' && (
                          <Badge variant="outline" className="bg-purple-100 text-purple-800">
                            AI Extracted
                          </Badge>
                        )}
                        {hlo.status === 'approved' && (
                          <Badge variant="outline" className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {hlo.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => approveHLO(hlo.id)}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingHLO(hlo.id)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeHLO(hlo.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t pt-4 mt-4">
            <Label className="mb-2 block font-semibold">Add Your Own Objective</Label>
            <div className="flex gap-2">
              <Textarea
                placeholder="e.g., Improve incident response time by 50%"
                value={newHLOText}
                onChange={(e) => setNewHLOText(e.target.value)}
                className="flex-1"
                rows={2}
              />
              <Button
                onClick={addHLO}
                disabled={!newHLOText.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <Alert>
            <AlertDescription>
              <strong>{approvedHLOs.length}</strong> objective{approvedHLOs.length !== 1 ? 's' : ''} approved. 
              You can continue when you're ready.
            </AlertDescription>
          </Alert>
        </div>
      )
    },
    {
      title: 'Invite Your Team',
      icon: Users,
      content: (
        <div className="space-y-4">
          <p>
            Invite team members who will participate in the discovery process. 
            They'll answer questions about workflows, processes, and requirements.
          </p>

          {teamMembers.length > 0 && (
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {teamMembers.map((member, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-white border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{member.name}</p>
                      <p className="text-xs text-gray-600">{member.email}</p>
                      {member.role && (
                        <p className="text-xs text-gray-500 mt-0.5">{member.role}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTeamMember(index)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="border rounded-lg p-4 bg-gray-50">
            <Label className="mb-3 block font-semibold">Add Team Member</Label>
            <div className="space-y-3">
              <div>
                <Label htmlFor="member-name" className="text-sm">Name</Label>
                <Input
                  id="member-name"
                  placeholder="John Smith"
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="member-email" className="text-sm">Email</Label>
                <Input
                  id="member-email"
                  type="email"
                  placeholder="john.smith@company.com"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="member-role" className="text-sm">Role (Optional)</Label>
                <Input
                  id="member-role"
                  placeholder="e.g., IT Manager, Service Desk Lead"
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                />
              </div>
              <Button
                onClick={addTeamMember}
                disabled={!newMemberName.trim() || !newMemberEmail.trim()}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Team Member
              </Button>
            </div>
          </div>

          <Alert>
            <AlertDescription>
              <strong>{teamMembers.length}</strong> team member{teamMembers.length !== 1 ? 's' : ''} invited. 
              They'll receive an email invitation to join the discovery process.
            </AlertDescription>
          </Alert>

          <div className="text-sm text-gray-600">
            <p>
              <strong>Note:</strong> You can invite more team members later from your engagement dashboard.
            </p>
          </div>
        </div>
      )
    },
    {
      title: 'Ready to Begin',
      icon: CheckCircle,
      content: (
        <div className="space-y-4">
          <p>
            Great! Your onboarding is complete. Here's a summary of what happens next:
          </p>

          <div className="space-y-3">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Objectives Approved
              </h4>
              <p className="text-sm text-gray-700">
                You've approved <strong>{approvedHLOs.length}</strong> high-level objective{approvedHLOs.length !== 1 ? 's' : ''}. 
                Your consultant will use these to configure your {platform} system.
              </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Team Invitations Sent
              </h4>
              <p className="text-sm text-gray-700">
                {teamMembers.length === 0 
                  ? "You haven't invited any team members yet. You can do this later from your dashboard."
                  : `${teamMembers.length} team member${teamMembers.length !== 1 ? 's' : ''} will receive invitation emails to participate in discovery.`
                }
              </p>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-purple-600" />
                Discovery Phase Begins
              </h4>
              <p className="text-sm text-gray-700">
                You and your team will now answer discovery questions to help configure your {platform} system. 
                This process typically takes 1-2 weeks.
              </p>
            </div>
          </div>

          <Alert>
            <AlertDescription>
              <strong>Your consultant has been notified</strong> that you've completed onboarding. 
              They may reach out to schedule a kickoff call.
            </AlertDescription>
          </Alert>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold mb-2">Next Steps</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Navigate to your engagement dashboard</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Review the discovery grid and start answering questions</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Monitor your team's progress</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Contact {consultantName} if you have questions</span>
              </li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  const currentStep = steps[step];
  const Icon = currentStep.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
      <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle>{currentStep.title}</CardTitle>
                <CardDescription>
                  Step {step + 1} of {steps.length}
                </CardDescription>
              </div>
            </div>
            {onSkip && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onSkip}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            />
          </div>
        </CardHeader>

        <CardContent>
          <div className="mb-6">
            {currentStep.content}
          </div>

          <div className="flex gap-3">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
              >
                Back
              </Button>
            )}
            
            <div className="flex-1" />

            {step < steps.length - 1 ? (
              <>
                {onSkip && (
                  <Button
                    variant="ghost"
                    onClick={onSkip}
                  >
                    Skip
                  </Button>
                )}
                <Button
                  onClick={() => setStep(step + 1)}
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            ) : (
              <Button
                onClick={handleComplete}
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
              >
                Complete Onboarding <Rocket className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}